/**
 * PainelDoSino — o conteúdo do sino (parte D1, 01/10/2026), o MESMO no Popover (desktop) e na folha de baixo (celular):
 * cabeçalho, "Som neste aparelho", a lista e o rodapé com "Ver todas" e "Preferências". Peças B e C do desenho.
 *
 * D3 (passo 19, peça G): a FONTE dos dados, os destinos, o validador do link, o item e os textos vêm por prop. Os padrões são os do
 * interno (`useNotifications()` é lido pelo `NotificationBell`, que passa a `fonte`); o sino do portal passa os dele. A lista é uma
 * `<ul>`: o `Item` do portal é um botão solto.
 */
import React, { type ComponentType, useId } from "react";
import { Settings2, Volume2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useSomDoAparelho } from "@/hooks/useSomDoAparelho";
import { caminhoSeguroInterno } from "@/lib/caminhoSeguroInterno";
import { audioDisponivel, tocarSomDeAviso } from "@/lib/somDeAviso";
import { cn } from "@/lib/utils";
import NotificationItem from "./NotificationItem";

/** O que o painel lê de uma notificação (`NotificationInboxItem` e `PortalNotification` cabem por estrutura). */
export interface ItemDoSino {
  id: string;
  title: string;
  body?: string | null;
  severity?: string | null;
  module?: string | null;
  event_type?: string;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
  link?: string | null;
}

export interface FonteDoSino {
  notifications: ItemDoSino[];
  unreadCount: number;
  isLoading: boolean;
  /** A busca falhou. Só aparece se não há itens para mostrar. */
  isError?: boolean;
  refetch?: () => void;
  markAsRead: (id: string) => unknown;
  markAllRead: () => unknown;
}

export interface PropsDoItemDoSino {
  notification: ItemDoSino;
  onRead: (id: string) => void;
}

export interface TextosDoSino {
  carregando: string;
  vazioTitulo: string;
  vazioDetalhe: string;
  erroTitulo: string;
  tentarDeNovo: string;
}

const TEXTOS_INTERNOS: TextosDoSino = {
  carregando: "Carregando...",
  vazioTitulo: "Nenhuma notificação",
  vazioDetalhe: "Tudo tranquilo por aqui",
  erroTitulo: "Não foi possível carregar as notificações.",
  tentarDeNovo: "Tentar de novo",
};

function ItemInterno({ notification, onRead }: PropsDoItemDoSino) {
  return <NotificationItem notification={notification} onRead={onRead} />;
}

interface Props {
  titulo: React.ReactNode;
  aoFechar: () => void;
  /** Na folha do celular o X do Sheet fica no canto do cabeçalho: abre espaço para ele. */
  compacto?: boolean;
  fonte: FonteDoSino;
  hrefVerTodas?: string;
  hrefPreferencias?: string;
  /** Valida o link do item antes de navegar (padrão: o interno, que recusa `/portal/*`). */
  caminhoSeguro?: (link?: string | null) => string | null;
  Item?: ComponentType<PropsDoItemDoSino>;
  textos?: TextosDoSino;
  /** `data-testid` do "Marcar todas como lidas" e da lista (o portal mantém os dele). */
  idsDeTeste?: { marcarTodas?: string; lista?: string };
}

export function PainelDoSino({
  titulo,
  aoFechar,
  compacto = false,
  fonte,
  hrefVerTodas = "/notificacoes/inbox",
  hrefPreferencias = "/notifications/preferences",
  caminhoSeguro = caminhoSeguroInterno,
  Item = ItemInterno,
  textos = TEXTOS_INTERNOS,
  idsDeTeste,
}: Props) {
  const { notifications, unreadCount, isLoading, isError, refetch } = fonte;
  const navigate = useNavigate();
  const [somLigado, definirSom] = useSomDoAparelho();
  const idDoSom = useId();
  const semItens = notifications.length === 0;

  const aoLer = (id: string) => {
    fonte.markAsRead(id);
    aoFechar();
    // Parte C: o aviso com link abre o destino (ex.: o post do feed).
    const destino = caminhoSeguro(notifications.find((n) => n.id === id)?.link);
    if (destino) navigate(destino);
  };

  const trocarSom = (ligado: boolean) => {
    definirSom(ligado);
    // Ligar toca o plim na hora: confirma "é este o som" e, dentro do clique, libera o áudio.
    if (ligado) tocarSomDeAviso("aviso", { noGesto: true });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          "flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4",
          compacto && "pr-12"
        )}
      >
        {titulo}
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            // Na folha do celular o alvo de toque tem 44 px (spec §6.8); o cabeçalho (h-12) comporta. No desktop segue h-9.
            className={cn("h-9 text-xs", compacto && "min-h-[44px]")}
            onClick={() => fonte.markAllRead()}
            data-testid={idsDeTeste?.marcarTodas}
          >
            Marcar todas como lidas
          </Button>
        )}
      </div>
      {audioDisponivel() && (
        <div className="flex shrink-0 items-center gap-3 border-b bg-muted/40 px-4 py-2.5">
          <Volume2 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          <label htmlFor={idDoSom} className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Som neste aparelho</span>
            <span className="block text-[11px] text-muted-foreground">Toca só neste navegador</span>
          </label>
          <Switch id={idDoSom} checked={somLigado} onCheckedChange={trocarSom} />
        </div>
      )}
      <div className={cn("min-h-0 flex-1 overflow-y-auto", !compacto && "max-h-[400px]")}>
        {isLoading && semItens && (
          <div className="py-8 text-center text-sm text-muted-foreground">{textos.carregando}</div>
        )}
        {!isLoading && semItens && isError && (
          <div role="alert" className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">{textos.erroTitulo}</p>
            {refetch && (
              <button
                type="button"
                onClick={() => refetch()}
                className={cn(
                  "mt-1 inline-flex items-center text-sm text-primary underline-offset-2 hover:underline",
                  compacto && "min-h-[44px]"
                )}
              >
                {textos.tentarDeNovo}
              </button>
            )}
          </div>
        )}
        {!isLoading && semItens && !isError && (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">{textos.vazioTitulo}</p>
            <p className="text-xs text-muted-foreground">{textos.vazioDetalhe}</p>
          </div>
        )}
        {!semItens && (
          <ul className="m-0 list-none p-0" data-testid={idsDeTeste?.lista}>
            {notifications.map((notif) => (
              <li key={notif.id}>
                <Item notification={notif} onRead={aoLer} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex h-12 shrink-0 items-center justify-between border-t px-4">
        {/* No celular (folha) o alvo de toque tem 44 px (spec §6.8); no desktop o link segue com a
            altura do texto. `inline-flex min-h` no lugar de `py`: o rodapé tem altura fixa (h-12). */}
        <Link
          to={hrefVerTodas}
          onClick={aoFechar}
          className={cn(
            "text-sm text-primary hover:underline",
            compacto && "inline-flex min-h-[44px] items-center"
          )}
        >
          Ver todas
        </Link>
        <Link
          to={hrefPreferencias}
          onClick={aoFechar}
          className={cn(
            "inline-flex items-center gap-1 text-sm text-primary hover:underline",
            compacto && "min-h-[44px]"
          )}
        >
          <Settings2 aria-hidden="true" className="h-4 w-4" />
          Preferências
        </Link>
      </div>
    </div>
  );
}
